import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { withDeduplication } from '../utils/apiCache';
import { shouldSuppressSound } from '../utils/quietHours';
import { getDeviceSignals } from '../utils/deviceSignals';

const AuthContext = createContext(null);

// Sent on every request (incl. login/refresh) so the backend can derive a strong,
// per-physical-device fingerprint — browsers cannot expose MAC addresses.
try { axios.defaults.headers.common['X-Device-Signals'] = getDeviceSignals(); } catch { /* ignore */ }

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

// Decode the user_id from a JWT (no verification — display/guard use only).
const tokenUserId = (t) => {
  try { return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).user_id || null; } catch (e) { return null; }
};

const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

export const CHAT_SOUND_OPTIONS = [
  { id: 'default', label: 'Default' },
  { id: 'soft-ping', label: 'Soft ping' },
  { id: 'bell', label: 'Bell' },
  { id: 'chime', label: 'Chime' },
  { id: 'silent', label: 'Silent' },
];

const CHAT_SOUND_IDS = new Set(CHAT_SOUND_OPTIONS.map(option => option.id));
const normalizeChatSoundChoice = (value) => (CHAT_SOUND_IDS.has(value) ? value : 'default');

const CHAT_SOUND_PROFILES = {
  default: [
    { frequency: 880, type: 'sine', start: 0, duration: 0.12, volume: 0.3 },
    { frequency: 660, type: 'sine', start: 0.12, duration: 0.18, volume: 0.3 },
  ],
  'soft-ping': [
    { frequency: 740, type: 'sine', start: 0, duration: 0.18, volume: 0.16 },
  ],
  bell: [
    { frequency: 1046.5, type: 'triangle', start: 0, duration: 0.2, volume: 0.24 },
    { frequency: 784, type: 'triangle', start: 0.11, duration: 0.34, volume: 0.18 },
  ],
  chime: [
    { frequency: 659.25, type: 'sine', start: 0, duration: 0.12, volume: 0.22 },
    { frequency: 880, type: 'sine', start: 0.1, duration: 0.14, volume: 0.2 },
    { frequency: 1174.66, type: 'sine', start: 0.22, duration: 0.22, volume: 0.17 },
  ],
};

const playChatSoundProfile = (ctx, soundChoice) => {
  const profile = CHAT_SOUND_PROFILES[soundChoice] || CHAT_SOUND_PROFILES.default;
  const now = ctx.currentTime;
  profile.forEach((tone) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = tone.frequency;
    oscillator.type = tone.type;
    const startAt = now + tone.start;
    const stopAt = startAt + tone.duration;
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(tone.volume, startAt + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.01, stopAt);
    oscillator.start(startAt);
    oscillator.stop(stopAt + 0.02);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Check for follow_window_token first (from "Follow in New Window" feature)
    const followToken = localStorage.getItem('follow_window_token');
    if (followToken) {
      // Move to sessionStorage for this tab and clean up
      sessionStorage.setItem('token', followToken);
      localStorage.removeItem('follow_window_token');
      return followToken;
    }
    // Quick-login sessions are stored in sessionStorage ('quick_login_token') so they
    // stay isolated to THIS tab and never overwrite the shared localStorage token
    // (which would hijack every other open tab). Precedence mirrors impersonation.
    return sessionStorage.getItem('impersonate_token') || sessionStorage.getItem('quick_login_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
  });
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const deviceWsRef = useRef(null);
  const connectWebSocketRef = useRef(null);        // latest connectWebSocket (fresh token) for reconnects
  const connectDeviceWebSocketRef = useRef(null);  // latest connectDeviceWebSocket (fresh token)
  const tokenRef = useRef(null);                   // latest token, for reconnect closures
  const [wsConnected, setWsConnected] = useState(false);
  const [deviceWsConnected, setDeviceWsConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const deviceReconnectTimeoutRef = useRef(null);
  const deviceUpdateCallbacks = useRef(new Set());
  const lastChatPingRef = useRef(Date.now());
  const chatHeartbeatRef = useRef(null);
  const audioContextRef = useRef(null);
  const userInteractedRef = useRef(false);
  
  // Determine if this is an impersonated session (uses sessionStorage for isolation)
  const isImpersonatedSession = useRef(!!sessionStorage.getItem('impersonate_token'));
  // Quick-login (magic-link) sessions are also tab-isolated via sessionStorage so
  // opening a link never overwrites another tab's primary session.
  const isQuickLoginSession = useRef(!!sessionStorage.getItem('quick_login_token'));

  // Sliding-session / silent refresh plumbing. refreshPromiseRef enforces a single
  // in-flight refresh (request queueing) so concurrent 401s don't trigger a refresh storm.
  const refreshPromiseRef = useRef(null);
  const sessionExpiredRef = useRef(false);   // guards against duplicate "session expired" toasts/logouts
  const apiErrorHandlerRef = useRef(null);   // latest response-interceptor handler (set each render)
  
  // Global unread message count
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  
  // Chat page enabled state (controlled by feature flags)
  const [chatEnabled, setChatEnabled] = useState(true);
  
  // Chat notification sound
  const [chatSoundChoice, setChatSoundChoiceState] = useState(() => {
    const savedChoice = localStorage.getItem('chat_sound_choice');
    if (savedChoice) return normalizeChatSoundChoice(savedChoice);
    const legacyEnabled = localStorage.getItem('chat_sound_enabled');
    return legacyEnabled === 'false' ? 'silent' : 'default';
  });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const savedChoice = localStorage.getItem('chat_sound_choice');
    if (savedChoice) return normalizeChatSoundChoice(savedChoice) !== 'silent';
    const saved = localStorage.getItem('chat_sound_enabled');
    return saved !== null ? saved === 'true' : true;
  });

  const setChatSoundChoice = useCallback((choice) => {
    const normalized = normalizeChatSoundChoice(choice);
    setChatSoundChoiceState(normalized);
    setSoundEnabled(normalized !== 'silent');
  }, []);
  
  // Save sound preference to localStorage
  useEffect(() => {
    localStorage.setItem('chat_sound_enabled', soundEnabled);
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem('chat_sound_choice', chatSoundChoice);
  }, [chatSoundChoice]);
  
  // Initialize AudioContext on first user interaction
  useEffect(() => {
    const initAudioContext = () => {
      userInteractedRef.current = true;
      // Remove listeners after first interaction — no need to keep firing
      events.forEach(event => {
        document.removeEventListener(event, initAudioContext);
      });
    };
    
    // Listen for any user interaction to mark as interacted
    const events = ['click', 'touchstart', 'keydown', 'mousedown'];
    events.forEach(event => {
      document.addEventListener(event, initAudioContext, { passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, initAudioContext);
      });
    };
  }, []);
  
  const playNotificationSound = useCallback((soundChoiceOverride = null) => {
    const selectedSound = normalizeChatSoundChoice(soundChoiceOverride || chatSoundChoice);
    const isPreview = Boolean(soundChoiceOverride);
    if (isPreview) userInteractedRef.current = true;
    if (selectedSound === 'silent' || (!isPreview && !soundEnabled) || !userInteractedRef.current) return;
    if (!isPreview && shouldSuppressSound()) return;
    
    try {
      // Lazily create AudioContext only when sound is actually needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      
      // Resume if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => playChatSoundProfile(audioContext, selectedSound));
        return;
      }
      
      playChatSoundProfile(audioContext, selectedSound);
    } catch (e) {
      // Sound playback failed silently
    }
  }, [chatSoundChoice, soundEnabled]);

  // Memoize the axios instance so its reference is stable across re-renders.
  // Without this, EVERY chat message triggers setTotalUnreadMessages →
  // AuthContext re-render → new api reference → every consumer's useCallback
  // depending on `api` recreates → cascading re-fetches that on iOS Safari
  // cause push subscription state to flicker OFF (transient null from
  // pushManager.getSubscription during the race). Recompute only when the
  // auth token actually changes (login / logout).
  const api = useMemo(() => {
    const instance = withDeduplication(axios.create({
      baseURL: `${API_URL}/api`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }));
    // Global 401 handler: transparently refresh the access token + retry once, else
    // gracefully end the session. Delegates to the latest handler stored in a ref so
    // the logic always sees current state despite the instance being memoized on token.
    instance.interceptors.response.use(
      (resp) => resp,
      (error) => (apiErrorHandlerRef.current ? apiErrorHandlerRef.current(error, instance) : Promise.reject(error))
    );
    return instance;
  }, [token]);

  // Fetch total unread messages count
  const fetchUnreadCount = useCallback(async () => {
    if (!token || !user?.id) return;
    try {
      const response = await api.get('/conversations/unread-total');
      setTotalUnreadMessages(Number(response.data?.total_unread || 0));
      // Server-authoritative muted list — keeps every device silenced without visiting /chat
      if (Array.isArray(response.data?.muted_conversation_ids)) {
        localStorage.setItem('muted_conversation_ids', JSON.stringify(response.data.muted_conversation_ids));
      }
    } catch (e) {
      // Unread count fetch failed silently
    }
  }, [api, token, user?.id]);

  // Fetch unread count when user logs in
  useEffect(() => {
    if (user && token) {
      fetchUnreadCount();
    }
  }, [user, token, fetchUnreadCount]);

  // One-time push subscription upgrade: legacy FCM endpoints ignore the
  // Urgency header, delaying incoming-call pushes on Android with screen off.
  useEffect(() => {
    if (!user?.id || !token) return undefined;
    const t = setTimeout(() => {
      import('../utils/pushNotifications').then(({ migrateLegacySubscription }) => {
        migrateLegacySubscription(api).then((migrated) => {
          if (migrated) console.log('Push subscription upgraded to modern endpoint');
        });
      });
    }, 3000);
    return () => clearTimeout(t);
  }, [user?.id, token, api]);

  // Keep the header Chat pill server-authoritative across tabs/devices.
  useEffect(() => {
    if (!user || !token) return undefined;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    };
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [user, token, fetchUnreadCount]);

  // Safety net for multi-device same-user sessions where a WebSocket event may be missed.
  useEffect(() => {
    if (!user || !token) return undefined;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [user, token, fetchUnreadCount]);

  // Chat WebSocket connection — handles chat messages only
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;
    // Stale-closure guard: a slow async caller (e.g. fetchUser started before an
    // account switch) may invoke a connect fn captured with the OLD token. Opening
    // a socket with it would register this tab under the WRONG user on the server
    // (other account's toasts/chat leak into this window) — bail if outdated.
    if (token !== tokenRef.current) return;

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/${token}`);
      
      ws.onopen = () => {
        debugLog('Chat WebSocket connected');
        setWsConnected(true);
        lastChatPingRef.current = Date.now();
        // Start heartbeat monitor — force reconnect if no server message within 45s
        if (chatHeartbeatRef.current) clearInterval(chatHeartbeatRef.current);
        chatHeartbeatRef.current = setInterval(() => {
          const elapsed = Date.now() - lastChatPingRef.current;
          if (elapsed > 45000) {
            console.warn(`Chat WS heartbeat timeout (${Math.round(elapsed/1000)}s). Forcing reconnect.`);
            ws.close();
          }
        }, 10000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // Track any server message as proof of life
          lastChatPingRef.current = Date.now();

          // Identity check (server truth): if this socket is authenticated as a
          // different user than this tab's CURRENT token, tear it down — the
          // guarded onclose reconnects with the latest token.
          if (message.type === 'connected') {
            const expected = tokenRef.current ? tokenUserId(tokenRef.current) : null;
            if (message.user_id && expected && message.user_id !== expected) {
              console.warn('Chat WS identity mismatch — closing stale-identity socket');
              try { ws.close(); } catch (e) {}
              return;
            }
          }
          
          // Handle chat messages
          if (message.type === 'chat_message') {
            const msg = message.message;
            
            if (msg && msg.sender_id !== user?.id && chatEnabled) {
              let isMutedConv = false;
              try { isMutedConv = (JSON.parse(localStorage.getItem('muted_conversation_ids') || '[]')).includes(message.conversation_id); } catch (e) {}
              const content = msg.type === 'text' ? msg.content : 
                            msg.type === 'file' ? '📎 File' :
                            msg.type === 'voice' ? '🎤 Voice message' :
                            msg.type === 'location' ? '📍 Location' : 'Message';
              
              const isOnChatPage = window.location.pathname === '/chat';
              // Skip the in-app toaster on mobile (≤1023px) — the OS-level push
              // notification handles user attention there. Desktop keeps the
              // sonner toast because mobile-style banners aren't as visible.
              const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
              
              if (!isOnChatPage && !isMobile && !isMutedConv) {
                toast(`${msg.sender_name}`, {
                  description: content,
                  duration: 8000,
                  className: 'bg-[#2563eb] text-white border-none',
                  action: {
                    label: 'View',
                    onClick: () => window.location.href = '/chat'
                  }
                });
              }
              if (!isOnChatPage) {
                if (!isMutedConv) playNotificationSound();
                setTotalUnreadMessages(prev => prev + 1);
              }
              setTimeout(() => fetchUnreadCount(), 300);
            }
            
            // Notify chat callbacks
            deviceUpdateCallbacks.current.forEach(callback => {
              callback(null, message);
            });
          }
          
          // Forward conversation events (created/deleted/participant changes), reaction updates, message deletions, typing indicators and read receipts to chat callbacks
          if (message.type === 'conversation_created' || message.type === 'conversation_deleted' || message.type === 'conversation_updated' || message.type === 'reaction_update' || message.type === 'chat_message_deleted' || message.type === 'link_preview_update' || message.type === 'participants_changed' || message.type === 'participant_removed' || message.type === 'typing' || message.type === 'read_receipt' || message.type === 'call_invite' || message.type === 'call_accepted' || message.type === 'call_declined' || message.type === 'call_ended' || message.type === 'call_signal' || (typeof message.type === 'string' && message.type.startsWith('group_call'))) {
            deviceUpdateCallbacks.current.forEach(callback => {
              callback(null, message);
            });
            if (message.type === 'conversation_created' || message.type === 'conversation_deleted' || message.type === 'participants_changed' || message.type === 'participant_removed' || message.type === 'read_receipt') {
              setTimeout(() => fetchUnreadCount(), 200);
            }
          }

          // Forward Event Engine alerts to callbacks (for EventNotificationBell sound + toast + pill/card)
          if (message.type === 'alert' || message.type === 'event_occurrence' || message.type === 'event_occurrence_cleared' || message.type === 'driver_confirmation_alert' || message.type === 'audit_alert') {
            deviceUpdateCallbacks.current.forEach(callback => {
              callback(message.device_id || null, message);
            });
          }
          // Ignore server pings and other non-chat messages on this connection
        } catch (e) {
          console.error('Chat WS message parse error:', e);
        }
      };

      ws.onclose = () => {
        debugLog('Chat WebSocket disconnected');
        if (wsRef.current !== ws) return;  // a newer socket already took over (e.g. token rotated)
        setWsConnected(false);
        wsRef.current = null;
        if (chatHeartbeatRef.current) clearInterval(chatHeartbeatRef.current);
        if (tokenRef.current) {
          // Reconnect via the LATEST connect fn so a rotated token is used, not the stale one.
          reconnectTimeoutRef.current = setTimeout(() => connectWebSocketRef.current?.(), 3000);
        }
      };

      ws.onerror = (error) => console.error('Chat WebSocket error:', error);
      wsRef.current = ws;
    } catch (error) {
      console.error('Chat WebSocket connection error:', error);
    }
  }, [token, user?.id, chatEnabled, playNotificationSound, fetchUnreadCount]);

  // Device WebSocket connection — handles GPS device updates only (dedicated channel)
  const connectDeviceWebSocket = useCallback(() => {
    if (!token || deviceWsRef.current?.readyState === WebSocket.OPEN || deviceWsRef.current?.readyState === WebSocket.CONNECTING) return;
    if (token !== tokenRef.current) return;  // stale-closure guard (see connectWebSocket)

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/devices/${token}`);
      
      ws.onopen = () => {
        debugLog('Device WebSocket connected');
        setDeviceWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Identity check (server truth) — mirror of the chat socket guard.
          if (message.type === 'connected') {
            const expected = tokenRef.current ? tokenUserId(tokenRef.current) : null;
            if (message.user_id && expected && message.user_id !== expected) {
              console.warn('Device WS identity mismatch — closing stale-identity socket');
              try { ws.close(); } catch (e) {}
              return;
            }
          }

          if (message.type === 'device_update') {
            deviceUpdateCallbacks.current.forEach(callback => {
              callback(message.device_id, message.data);
            });
          }
          // Ignore server pings on this connection
        } catch (e) {
          console.error('Device WS message parse error:', e);
        }
      };

      ws.onclose = () => {
        debugLog('Device WebSocket disconnected');
        if (deviceWsRef.current !== ws) return;  // a newer socket already took over (e.g. token rotated)
        setDeviceWsConnected(false);
        deviceWsRef.current = null;
        if (tokenRef.current) {
          deviceReconnectTimeoutRef.current = setTimeout(() => connectDeviceWebSocketRef.current?.(), 3000);
        }
      };

      ws.onerror = (error) => console.error('Device WebSocket error:', error);
      deviceWsRef.current = ws;
    } catch (error) {
      console.error('Device WebSocket connection error:', error);
    }
  }, [token]);

  // Keep refs pointed at the latest connect fns + token so reconnect closures (which may
  // live on a socket opened with a now-stale token) always use the freshest values.
  connectWebSocketRef.current = connectWebSocket;
  connectDeviceWebSocketRef.current = connectDeviceWebSocket;
  tokenRef.current = token;

  // Subscribe to device updates via dedicated device WebSocket
  const subscribeToDevices = useCallback((deviceIds) => {
    if (deviceWsRef.current?.readyState === WebSocket.OPEN) {
      deviceWsRef.current.send(JSON.stringify({
        type: 'subscribe',
        device_ids: deviceIds
      }));
    }
  }, []);

  // Register callback for device updates
  const onDeviceUpdate = useCallback((callback) => {
    deviceUpdateCallbacks.current.add(callback);
    return () => {
      deviceUpdateCallbacks.current.delete(callback);
    };
  }, []);

  const login = async (email, password, rememberMe = true) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, refresh_token: newRefresh, session_id: newSessionId, user: userData } = response.data;
    // Store token + refresh token based on "Remember me" preference (same store as token).
    if (newSessionId) localStorage.setItem('session_id', newSessionId);
    if (rememberMe) {
      localStorage.setItem('token', newToken);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
      sessionStorage.removeItem('refresh_token');
    } else {
      sessionStorage.setItem('token', newToken);
      if (newRefresh) sessionStorage.setItem('refresh_token', newRefresh);
      localStorage.removeItem('refresh_token');
    }
    // Clear any impersonation / quick-login state on normal login
    localStorage.removeItem('impersonated_by');
    sessionStorage.removeItem('impersonate_token');
    sessionStorage.removeItem('impersonate_user');
    sessionStorage.removeItem('quick_login_token');
    sessionStorage.removeItem('quick_login_session_id');
    isImpersonatedSession.current = false;
    isQuickLoginSession.current = false;
    sessionExpiredRef.current = false;  // re-arm the session-expired guard for this fresh session
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Quick login using token link
  const quickLogin = useCallback(async (quickLoginToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/quick-login/${quickLoginToken}`);
      const { token: newToken, session_id: newSessionId, user: userData } = response.data;
      // Store the quick-login session in sessionStorage ONLY so it is isolated to
      // THIS tab. Writing the shared localStorage 'token' would overwrite (hijack)
      // every other open tab's session. sessionStorage survives a refresh in this
      // tab and is cleared when the tab closes.
      sessionStorage.setItem('quick_login_token', newToken);
      if (newSessionId) sessionStorage.setItem('quick_login_session_id', newSessionId);
      // Clear any impersonation state for this tab (mutually exclusive). The primary
      // localStorage 'token' (e.g. an admin's own session) is intentionally left untouched.
      sessionStorage.removeItem('impersonate_token');
      sessionStorage.removeItem('impersonate_user');
      isImpersonatedSession.current = false;
      isQuickLoginSession.current = true;
      setToken(newToken);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Quick login failed';
      return { success: false, error: errorMessage };
    }
  }, []);

  // Establish a session from a backend-issued token (used after account-activation /
  // accept-invite). TAB-ISOLATED via sessionStorage (mirrors quick-login): activating
  // via an invite link must NEVER overwrite the SHARED localStorage 'token' — doing so
  // hijacks every other tab/window that's logged in as a different user (they'd switch
  // to the just-activated user on their next refresh, since localStorage takes
  // precedence). This session survives a refresh in THIS tab and is cleared when the
  // tab closes; the user can sign back in anytime with the password they just set.
  const establishSession = useCallback((newToken, userData) => {
    sessionStorage.setItem('quick_login_token', newToken);
    sessionStorage.removeItem('impersonate_token');
    sessionStorage.removeItem('impersonate_user');
    isImpersonatedSession.current = false;
    isQuickLoginSession.current = true;
    setToken(newToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    if (isImpersonatedSession.current) {
      sessionStorage.removeItem('impersonate_token');
      sessionStorage.removeItem('impersonate_user');
    } else if (isQuickLoginSession.current) {
      // Tab-isolated quick-login session — only clear this tab's sessionStorage,
      // never the shared localStorage token used by other tabs.
      const qlToken = sessionStorage.getItem('quick_login_token');
      const qlSid = sessionStorage.getItem('quick_login_session_id');
      if (qlToken && qlSid) {
        // Best-effort: remove this quick-login device from Logged-in Devices.
        axios.delete(`${API_URL}/api/auth/sessions/${qlSid}`, {
          headers: { Authorization: `Bearer ${qlToken}` },
        }).catch(() => {});
      }
      sessionStorage.removeItem('quick_login_token');
      sessionStorage.removeItem('quick_login_session_id');
      isQuickLoginSession.current = false;
    } else {
      // Primary session: best-effort revoke the refresh token server-side, then clear
      // both token and refresh token from storage.
      const primaryToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
      if (refreshToken) {
        axios.post(`${API_URL}/api/auth/logout`, { refresh_token: refreshToken }, {
          headers: primaryToken ? { Authorization: `Bearer ${primaryToken}` } : {},
        }).catch(() => {});
      }
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      sessionStorage.removeItem('refresh_token');
    }
    setToken(null);
    setUser(null);
    
    // Close both WebSocket connections
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (deviceWsRef.current) {
      deviceWsRef.current.close();
      deviceWsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (deviceReconnectTimeoutRef.current) {
      clearTimeout(deviceReconnectTimeoutRef.current);
    }
    if (chatHeartbeatRef.current) {
      clearInterval(chatHeartbeatRef.current);
    }
  }, []);

  // Refresh tokens only exist for PRIMARY sessions. Impersonation / quick-login tabs are
  // intentionally short-lived and tab-isolated, so they never silently refresh — on expiry
  // they end gracefully (forcing a fresh "Login As" / re-open of the magic link).
  const getStoredRefreshToken = useCallback(() => {
    if (isImpersonatedSession.current || isQuickLoginSession.current) return null;
    return localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
  }, []);

  // Persist a freshly-rotated token pair into whichever store the primary session uses.
  const persistRefreshedTokens = useCallback((newToken, newRefresh) => {
    const usingLocal = !!localStorage.getItem('token');
    if (usingLocal) {
      localStorage.setItem('token', newToken);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
    } else {
      sessionStorage.setItem('token', newToken);
      if (newRefresh) sessionStorage.setItem('refresh_token', newRefresh);
    }
    setToken(newToken);
  }, []);

  // Single-flight token refresh: concurrent 401s all await the same in-flight promise.
  const refreshAccessToken = useCallback(() => {
    if (!refreshPromiseRef.current) {
      const rt = getStoredRefreshToken();
      const p = rt
        ? axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: rt }).then((resp) => {
            const { token: nt, refresh_token: nr, session_id: sid } = resp.data;
            if (sid) localStorage.setItem('session_id', sid);
            persistRefreshedTokens(nt, nr);
            return nt;
          })
        : Promise.reject(new Error('no_refresh_token'));
      refreshPromiseRef.current = p;
      p.finally(() => { refreshPromiseRef.current = null; }).catch(() => {});
    }
    return refreshPromiseRef.current;
  }, [getStoredRefreshToken, persistRefreshedTokens]);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;  // only once per dead session
    sessionExpiredRef.current = true;
    toast.error('Your session has expired. Please sign in again.');
    logout();
  }, [logout]);

  // Response-interceptor handler: on an auth-expiry 401, refresh + retry once; else end session.
  const handleApiError = useCallback(async (error, instance) => {
    const original = error.config || {};
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const url = original.url || '';
    if (status === 401 && detail === 'Quick login link revoked' && !sessionExpiredRef.current) {
      sessionExpiredRef.current = true;
      logout();
      window.location.replace('/link-revoked');
      return Promise.reject(error);
    }
    if (status === 401 && detail === 'Session signed out' && !sessionExpiredRef.current) {
      sessionExpiredRef.current = true;
      toast.error('This device was signed out remotely.');
      logout();
      return Promise.reject(error);
    }
    const isAuthExpiry = status === 401 && (detail === 'Token expired' || detail === 'Invalid token');
    if (!isAuthExpiry || original._retried
        || url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')) {
      return Promise.reject(error);
    }
    if (!getStoredRefreshToken()) {
      handleSessionExpired();
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      const newToken = await refreshAccessToken();
      original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
      return instance(original);
    } catch (e) {
      // Only a definitive rejection of the refresh token ends the session.
      // A transient failure (device just resumed, network not up yet, 5xx)
      // must NOT log the user out — the next interaction retries the refresh.
      if (e?.response?.status === 401) {
        handleSessionExpired();
      } else {
        error._transientRefreshFailure = true;
      }
      return Promise.reject(error);
    }
  }, [getStoredRefreshToken, refreshAccessToken, handleSessionExpired, logout]);

  // Keep the interceptor pointed at the latest handler (instance is memoized on `token`).
  apiErrorHandlerRef.current = handleApiError;


  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    const startedToken = token;
    try {
      const response = await api.get('/auth/me');
      // Stale-closure guard: if the tab's account switched while /auth/me was in
      // flight (quick-login, Login As, sign-in-as-different-user), this closure
      // holds the OLD token. Applying its user or opening sockets with it would
      // mis-key this tab as the previous account (cross-account toast leak).
      if (tokenRef.current !== startedToken) return;
      setUser(response.data);
      connectWebSocket();
      connectDeviceWebSocket();
      // Load timezone settings
      try {
        const { loadTimezoneSettings } = await import('../utils/timezone');
        await loadTimezoneSettings(api);
      } catch (e) { console.debug('Timezone settings load failed:', e); }
    } catch (error) {
      if (tokenRef.current !== startedToken) return;  // stale closure — never touch the new session
      console.error('Failed to fetch user:', error);
      // Only logout on definitive authentication errors (401), never on network
      // errors or a transient refresh failure right after the device resumes.
      // Skip when the interceptor already handled this 401 (e.g. quick-login
      // revoked): a second logout() would take the primary branch and wipe the
      // SHARED localStorage session of other tabs (iter842fix).
      if (error.response?.status === 401 && !error._transientRefreshFailure && !sessionExpiredRef.current) {
        logout();
      }
      // For other errors, keep the user logged in but show loading complete
    } finally {
      setLoading(false);
    }
  }, [api, token, logout, connectWebSocket, connectDeviceWebSocket]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Update api headers when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token, api]);

  // On a token ROTATION (silent refresh: both old & new present and different), tear down
  // WebSockets opened with the stale token so they reconnect with the fresh one. The guarded
  // onclose handlers null the refs and schedule a reconnect via the latest-token connect fns.
  const prevTokenRef = useRef(token);
  useEffect(() => {
    const prev = prevTokenRef.current;
    prevTokenRef.current = token;
    if (prev && token && prev !== token) {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (deviceReconnectTimeoutRef.current) clearTimeout(deviceReconnectTimeoutRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} }
      if (deviceWsRef.current) { try { deviceWsRef.current.close(); } catch (e) {} }
    }
  }, [token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (deviceWsRef.current) deviceWsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (deviceReconnectTimeoutRef.current) clearTimeout(deviceReconnectTimeoutRef.current);
      if (chatHeartbeatRef.current) clearInterval(chatHeartbeatRef.current);
    };
  }, []);

  const value = {
    user,
    setUser,  // Expose setUser to allow updating user data after profile changes
    token,
    loading,
    login,
    quickLogin,
    establishSession,
    logout,
    api,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin',
    isManager: user?.role === 'manager',
    isImpersonated: isImpersonatedSession.current,
    isQuickLogin: isQuickLoginSession.current,
    // WebSocket methods
    wsConnected: deviceWsConnected, // LiveTracking uses this for device subscriptions
    chatWsConnected: wsConnected,
    subscribeToDevices,
    onDeviceUpdate,
    // Send a JSON message over the chat WebSocket (typing events, read receipts, etc.)
    sendChatWs: (payload) => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify(payload));
          return true;
        }
      } catch {}
      return false;
    },
    // Global sound control
    soundEnabled,
    setSoundEnabled,
    chatSoundChoice,
    setChatSoundChoice,
    chatSoundOptions: CHAT_SOUND_OPTIONS,
    playNotificationSound,
    // Unread messages
    totalUnreadMessages,
    refreshUnreadCount: fetchUnreadCount,
    // Chat enabled control
    chatEnabled,
    setChatEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
